import { type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  LANGUAGE_OPTIONS,
  LANGUAGE_STORAGE_KEY,
  isLanguageCode,
  normalizeLanguageCode,
  type LanguageCode,
} from "@/i18n/languages";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { persistLanguageToDb } from "@/services/preferenceSync";

/** Display order + flags for compact Welcome/header selectors only. */
const LANGUAGE_FLAG: Record<LanguageCode, string> = {
  en: "🇺🇸",
  he: "🇮🇱",
  es: "🇪🇸",
  de: "🇩🇪",
  fr: "🇫🇷",
};

const COMPACT_LANGUAGE_OPTIONS: readonly { code: LanguageCode; label: string }[] = [
  { code: "en", label: "English" },
  { code: "he", label: "עברית" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
];

interface LanguageSelectorProps {
  className?: string;
  /** Compact dark control for Welcome header; default preserves Settings/About look. */
  variant?: "default" | "compact";
}

export function LanguageSelector({
  className,
  variant = "default",
}: LanguageSelectorProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const current = normalizeLanguageCode(i18n.language) ?? "en";
  const isCompact = variant === "compact";
  const options = isCompact ? COMPACT_LANGUAGE_OPTIONS : LANGUAGE_OPTIONS;

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const code = event.target.value;
    if (!isLanguageCode(code)) return;
    void i18n.changeLanguage(code);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    if (user?.id) {
      void persistLanguageToDb(user.id, code);
    }
  };

  return (
    <select
      value={current}
      onChange={handleChange}
      aria-label={t("settings.languageTitle")}
      className={cn(
        isCompact
          ? [
              "max-w-[9.5rem] shrink-0 appearance-none rounded-lg border border-slate-600/80",
              "bg-slate-900/80 py-1.5 pe-7 ps-2 text-xs font-medium text-slate-100",
              "shadow-sm backdrop-blur-sm",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70",
              // chevron
              "bg-[length:12px] bg-[position:right_0.45rem_center] bg-no-repeat",
              "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23cbd5e1%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')]",
              "rtl:bg-[position:left_0.45rem_center]",
            ]
          : [
              "rounded-md border border-input bg-background px-3 py-2 text-sm",
              "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            ],
        className,
      )}
    >
      {options.map(({ code, label }) => (
        <option key={code} value={code}>
          {isCompact ? `${LANGUAGE_FLAG[code]} ${label}` : label}
        </option>
      ))}
    </select>
  );
}

export type { LanguageCode };
